/**
 * Theme and language, before first paint — boot.js's behaviour, ported.
 *
 * The same three lines are inlined in index.html, and deliberately duplicated there rather than
 * imported: a module is deferred, and a deferred theme is a flash of the wrong one. This copy is
 * what the app reads and writes once it is running.
 *
 * No attribute means "follow the system", and the board is dark-first.
 */

export type Theme = "dark" | "light";
export type Lang = "en" | "zh-Hant";

const THEME_KEY = "bd.theme";
const LANG_KEY = "bd.lang";

/** Every read and write is wrapped: in a private window or with site data blocked, the accessor
 *  throws, and a console that cannot remember a choice must still render. */
export const remembered = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* nothing to do: the choice holds for this page and is forgotten on the next */
    }
  },
};

/** What is actually on screen, which is the saved choice or the system's. */
export function showing(): Theme {
  const set = document.documentElement.dataset.theme;
  if (set === "dark" || set === "light") return set;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function savedTheme(): Theme | null {
  const value = remembered.get(THEME_KEY);
  return value === "dark" || value === "light" ? value : null;
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  remembered.set(THEME_KEY, theme);
}

export function savedLang(): Lang {
  return remembered.get(LANG_KEY) === "zh-Hant" ? "zh-Hant" : "en";
}

export function applyLang(lang: Lang): void {
  document.documentElement.dataset.lang = lang;
  document.documentElement.lang = lang;
  remembered.set(LANG_KEY, lang);
}

/** The theme toggle names the ACT, not the state: a sun on the dark board, a moon on paper. */
export const TOGGLE_LABEL: Record<Lang, Record<Theme, string>> = {
  en: { light: "Switch to light", dark: "Switch to dark" },
  "zh-Hant": { light: "切換為淺色", dark: "切換為深色" },
};
